// ==========================================
// Store de jeu - Zustand
// ==========================================

import { create } from 'zustand';
import type { PowerUpType, QuestionCategory, QuestionDifficulty, GameSettings, QuestionOption } from '@shared/types';

interface RoomPlayer {
  odId: string;
  pseudo: string;
  avatar?: string;
  isHost: boolean;
  isReady: boolean;
}

interface Room {
  id: string;
  hostId: string;
  players: RoomPlayer[];
  settings: GameSettings;
  status: 'waiting' | 'starting' | 'playing' | 'finished';
}

interface CurrentQuestion {
  questionId?: number;
  questionNumber: number;
  totalQuestions: number;
  type: string;
  category: QuestionCategory;
  difficulty: QuestionDifficulty;
  text: string;
  imageUrl?: string;
  videoUrl?: string;
  audioUrl?: string;
  options?: QuestionOption[];
  timelineItems?: { id: string; text: string }[];
  correctTimelineOrder?: { id: string; text: string }[];
  timeLimit: number;
  cropX?: number;
  cropY?: number;
  zoomLevel?: number;
}

interface PlayerResult {
  odId: string;
  pseudo: string;
  answer: string | null;
  correct: boolean;
  responseTime: number | null;
  score: number;
  streak: number;
}

interface FinalRanking {
  rank: number;
  odId: string;
  pseudo: string;
  score: number;
  correctAnswers: number;
  wrongAnswers: number;
  avgResponseTime: number;
}

interface GameStore {
  // Room state
  room: Room | null;
  isInRoom: boolean;
  countdown: number;
  
  // Game state
  gameId: number | null;
  isPlaying: boolean;
  currentQuestion: CurrentQuestion | null;
  questionStartTime: number | null;
  hasAnswered: boolean;
  lastAnswerCorrect: boolean | null;
  correctAnswer: string | null;
  playerResults: PlayerResult[];
  
  // Player state
  score: number;
  streak: number;
  availablePowerUps: PowerUpType[];
  activePowerUps: { type: PowerUpType; expiresAt: number }[];
  
  // Final results
  isFinished: boolean;
  finalRanking: FinalRanking[];
  
  // Special modes
  mysteryCurrentMode: string | null;
  mysteryModeIcon: string | null;
  blindMode: boolean;
  blindRevealed: boolean;
  // Blitz mode
  isBlitzMode: boolean;
  blitzDuration: number;
  blitzTimePerQuestion: number;
  blitzRemainingTime: number;
  // Power-ups Phase 3
  oracleAnswer: string | null;
  oracleExpiresAt: number | null;
  timeFreezeUntil: number | null;
  copycatInfo: { leaderPseudo: string; leaderAnswer: string | null; message?: string } | null;
  sabotageNotification: { playerName: string; points: number; isSuccess: boolean } | null;
  
  // Actions - Room
  setRoom: (room: Room | null) => void;
  updateRoomSettings: (settings: Partial<GameSettings>) => void;
  addPlayer: (player: RoomPlayer) => void;
  removePlayer: (odId: string) => void;
  setPlayerReady: (odId: string, isReady: boolean) => void;
  setHost: (odId: string) => void;
  setCountdown: (countdown: number) => void;
  resetPlayersReady: () => void;
  
  // Actions - Game
  startGame: (gameId: number) => void;
  setCurrentQuestion: (question: CurrentQuestion, serverTimestamp?: number) => void;
  submitAnswer: () => void;
  showAnswer: (correctAnswer: string, results: PlayerResult[], correctTimelineOrder?: any[]) => void;
  updateScore: (score: number, streak: number) => void;
  usePowerUp: (type: PowerUpType) => void;
  addActivePowerUp: (type: PowerUpType, expiresAt: number) => void;
  finishGame: (ranking: FinalRanking[]) => void;
  
  // Actions - Special modes
  setMysteryMode: (mode: string, icon: string) => void;
  setBlindReveal: (revealed: boolean) => void;
  setBlitzMode: (active: boolean, duration: number, timePerQuestion: number) => void;
  setBlitzRemainingTime: (time: number) => void;
  
  // Actions - Power-ups Phase 3
  setOracleReveal: (answer: string, duration: number) => void;
  setTimeFreeze: (duration: number) => void;
  setCopycatReveal: (leaderPseudo: string, leaderAnswer: string | null, message?: string) => void;
  setSabotageNotification: (playerName: string, points: number, isSuccess: boolean) => void;
  clearPowerUpEffects: () => void;
  
  // Power-up timer tracking
  _powerUpTimers: ReturnType<typeof setTimeout>[];
  clearAllPowerUpTimers: () => void;

  // Actions - Reset
  leaveRoom: () => void;
  reset: () => void;
}

const initialState = {
  room: null,
  isInRoom: false,
  countdown: 0,
  gameId: null,
  isPlaying: false,
  currentQuestion: null,
  questionStartTime: null,
  hasAnswered: false,
  lastAnswerCorrect: null,
  correctAnswer: null,
  playerResults: [],
  score: 0,
  streak: 0,
  availablePowerUps: [] as PowerUpType[],
  activePowerUps: [],
  isFinished: false,
  finalRanking: [],
  // Special modes
  mysteryCurrentMode: null,
  mysteryModeIcon: null,
  blindMode: false,
  blindRevealed: false,
  // Blitz mode
  isBlitzMode: false,
  blitzDuration: 60000,
  blitzTimePerQuestion: 3000,
  blitzRemainingTime: 60,
  // Power-ups Phase 3
  oracleAnswer: null as string | null,
  oracleExpiresAt: null as number | null,
  timeFreezeUntil: null as number | null,
  copycatInfo: null as { leaderPseudo: string; leaderAnswer: string | null; message?: string } | null,
  sabotageNotification: null as { playerName: string; points: number; isSuccess: boolean } | null,
  _powerUpTimers: [] as ReturnType<typeof setTimeout>[],
};

export const useGameStore = create<GameStore>((set, get) => ({
  ...initialState,
  
  // ========== Room Actions ==========
  setRoom: (room) => set({ room, isInRoom: !!room }),
  
  updateRoomSettings: (settings) => {
    const { room } = get();
    if (room) {
      set({ room: { ...room, settings: { ...room.settings, ...settings } } });
    }
  },
  
  addPlayer: (player) => {
    const { room } = get();
    if (room && !room.players.find(p => p.odId === player.odId)) {
      set({ room: { ...room, players: [...room.players, player] } });
    }
  },
  
  removePlayer: (odId) => {
    const { room } = get();
    if (room) {
      set({ room: { ...room, players: room.players.filter(p => p.odId !== odId) } });
    }
  },
  
  setPlayerReady: (odId, isReady) => {
    const { room } = get();
    if (room) {
      set({
        room: {
          ...room,
          players: room.players.map(p =>
            p.odId === odId ? { ...p, isReady } : p
          ),
        },
      });
    }
  },
  
  setHost: (odId) => {
    const { room } = get();
    if (room) {
      set({
        room: {
          ...room,
          hostId: odId,
          players: room.players.map(p => ({
            ...p,
            isHost: p.odId === odId,
          })),
        },
      });
    }
  },
  
  setCountdown: (countdown) => set({ countdown }),
  
  resetPlayersReady: () => {
    const { room } = get();
    if (room) {
      set({
        room: {
          ...room,
          status: 'waiting',
          players: room.players.map(p => ({
            ...p,
            isReady: p.isHost, // L'hôte reste prêt
          })),
        },
        isPlaying: false,
        isFinished: false,
        countdown: 0,
      });
    }
  },
  
  // ========== Game Actions ==========
  startGame: (gameId) => {
    set({
      gameId,
      isPlaying: true,
      isFinished: false,
      score: 0,
      streak: 0,
      availablePowerUps: [], // Populated by server via game:initialized
      activePowerUps: [],
      hasAnswered: false,
      lastAnswerCorrect: null,
      correctAnswer: null,
      playerResults: [],
      finalRanking: [],
    });
  },
  
  setCurrentQuestion: (question, serverTimestamp?: number) => {
    set({
      currentQuestion: question,
      questionStartTime: serverTimestamp || Date.now(),
      hasAnswered: false,
      lastAnswerCorrect: null,
      correctAnswer: null,
      playerResults: [],
    });
  },
  
  submitAnswer: () => set({ hasAnswered: true }),
  
  showAnswer: (correctAnswer, results, correctTimelineOrder?) => {
    const update: any = { correctAnswer, playerResults: results };
    // Store timeline correct order in the current question for display
    if (correctTimelineOrder) {
      const { currentQuestion } = get();
      if (currentQuestion) {
        update.currentQuestion = { ...currentQuestion, correctTimelineOrder };
      }
    }
    set(update);
  },
  
  updateScore: (score, streak) => set({ score, streak }),
  
  usePowerUp: (type) => {
    const { availablePowerUps } = get();
    const index = availablePowerUps.indexOf(type);
    if (index !== -1) {
      const newPowerUps = [...availablePowerUps];
      newPowerUps.splice(index, 1);
      set({ availablePowerUps: newPowerUps });
    }
  },
  
  addActivePowerUp: (type, expiresAt) => {
    const { activePowerUps } = get();
    set({ activePowerUps: [...activePowerUps, { type, expiresAt }] });
  },
  
  finishGame: (ranking) => {
    get().clearAllPowerUpTimers();
    set({
      isPlaying: false,
      isFinished: true,
      finalRanking: ranking,
      currentQuestion: null,
    });
  },
  
  // ========== Special Modes ==========
  setMysteryMode: (mode, icon) => set({
    mysteryCurrentMode: mode,
    mysteryModeIcon: icon,
  }),
  
  setBlindReveal: (revealed) => set({
    blindRevealed: revealed,
  }),
  
  setBlitzMode: (active, duration, timePerQuestion) => set({
    isBlitzMode: active,
    blitzDuration: duration,
    blitzTimePerQuestion: timePerQuestion,
  }),
  
  setBlitzRemainingTime: (time) => set({
    blitzRemainingTime: time,
  }),
  
  // ========== Power-ups Phase 3 ==========
  setOracleReveal: (answer, duration) => {
    set({
      oracleAnswer: answer,
      oracleExpiresAt: Date.now() + duration,
    });
    // Auto-clear après la durée
    const timerId = setTimeout(() => {
      set({ oracleAnswer: null, oracleExpiresAt: null });
    }, duration);
    set(state => ({ _powerUpTimers: [...state._powerUpTimers, timerId] }));
  },

  setTimeFreeze: (duration) => {
    set({
      timeFreezeUntil: Date.now() + duration,
    });
    // Auto-clear après la durée
    const timerId = setTimeout(() => {
      set({ timeFreezeUntil: null });
    }, duration);
    set(state => ({ _powerUpTimers: [...state._powerUpTimers, timerId] }));
  },

  setCopycatReveal: (leaderPseudo, leaderAnswer, message) => {
    set({
      copycatInfo: { leaderPseudo, leaderAnswer, message },
    });
    // Auto-clear après 5 secondes
    const timerId = setTimeout(() => {
      set({ copycatInfo: null });
    }, 5000);
    set(state => ({ _powerUpTimers: [...state._powerUpTimers, timerId] }));
  },

  setSabotageNotification: (playerName, points, isSuccess) => {
    set({
      sabotageNotification: { playerName, points, isSuccess },
    });
    // Auto-clear après 3 secondes
    const timerId = setTimeout(() => {
      set({ sabotageNotification: null });
    }, 3000);
    set(state => ({ _powerUpTimers: [...state._powerUpTimers, timerId] }));
  },
  
  clearPowerUpEffects: () => set({
    oracleAnswer: null,
    oracleExpiresAt: null,
    timeFreezeUntil: null,
    copycatInfo: null,
    sabotageNotification: null,
  }),

  clearAllPowerUpTimers: () => {
    const { _powerUpTimers } = get();
    _powerUpTimers.forEach(id => clearTimeout(id));
    set({ _powerUpTimers: [] });
  },
  
  // ========== Reset ==========
  leaveRoom: () => set({
    room: null,
    isInRoom: false,
    gameId: null,
    isPlaying: false,
    isFinished: false,
    currentQuestion: null,
    mysteryCurrentMode: null,
    mysteryModeIcon: null,
    blindMode: false,
    blindRevealed: false,  }),
  
  reset: () => {
    get().clearAllPowerUpTimers();
    set(initialState);
  },
}));